import { useState } from 'react';
import { CalendarRange, MapPin, RefreshCw, TriangleAlert } from 'lucide-react';
import {
  getApiError,
  useGetMyEventsQuery,
  useRespondToEventMutation,
  type ParentCalendarEvent,
  type RsvpAnswer,
} from '@/store/api';
import { Mark } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageLoader } from '@/components/ui/Spinner';
import { EVENT_CATEGORY_LABELS, formatEventWhen, relativeDay } from '@/lib/datetime';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

/*
  The school calendar, as a parent sees it.

  ── Why the reply is per child, with the child named ───────────────────────

  A parent with three children is routinely sending one and not the others, so
  "are you coming?" is unanswerable as a single question. Each child gets their
  own row with their own name on it — which also means a parent of one child
  sees exactly one row and nothing to think about.

  ── Why a closed event still shows what was answered ───────────────────────

  After the deadline the buttons go away but the answer stays visible. "Did I
  say yes to the sports day?" is the question a parent has at 7am on the day,
  and it is the one a disabled form still answers.
*/
export function ParentEventsPage() {
  const { data: events = [], isLoading, isError, error, isFetching, refetch } = useGetMyEventsQuery();

  if (isLoading) return <PageLoader label="Loading events…" />;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className={styles.pageTitle}>Events</h1>
          <p className={cn(styles.meta, 'mt-0.5')}>What is coming up at school.</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label="Refresh"
          icon={<RefreshCw size={14} className={isFetching ? 'animate-spin' : undefined} />}
          onClick={() => void refetch()}
        />
      </div>

      {isError ? (
        <div className={cn(styles.panel, 'overflow-clip')}>
          <EmptyState
            icon={<TriangleAlert size={17} />}
            title="Could not load events"
            description={getApiError(error)}
            action={
              <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        </div>
      ) : events.length === 0 ? (
        <div className={cn(styles.panel, 'overflow-clip')}>
          <EmptyState
            icon={<CalendarRange size={17} />}
            title="Nothing coming up"
            description="Sports days, parent meetings and holidays appear here once the school announces them."
          />
        </div>
      ) : (
        events.map((event) => <EventCard key={event._id} event={event} />)
      )}
    </div>
  );
}

function EventCard({ event }: { event: ParentCalendarEvent }) {
  const cancelled = event.status === 'cancelled';

  return (
    <div className={cn(styles.panel, cancelled && 'opacity-75')}>
      <div className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className={cn('text-[13px] font-bold text-foreground', cancelled && 'line-through')}>
            {event.title}
          </h2>
          {cancelled && <Mark tone="danger">Cancelled</Mark>}
          <Mark tone="neutral">{EVENT_CATEGORY_LABELS[event.category] ?? event.category}</Mark>
        </div>

        <p className={cn(styles.meta, 'mt-1')}>
          {formatEventWhen(event.startAt, event.endAt, event.allDay)}
          <span className="opacity-70"> · {relativeDay(event.startAt)}</span>
        </p>

        {event.location && (
          <p className={cn(styles.meta, 'mt-0.5 flex items-center gap-1')}>
            <MapPin size={11} /> {event.location}
          </p>
        )}

        {event.description && (
          <p className="mt-1.5 text-[11.5px] leading-relaxed text-muted-foreground">{event.description}</p>
        )}

        {cancelled && event.cancellationReason && (
          <p className={cn(styles.markDanger, 'mt-2 inline-flex normal-case tracking-normal text-[11px]')}>
            {event.cancellationReason}
          </p>
        )}
      </div>

      {event.rsvpRequired && !cancelled && (
        <div className="border-t border-border px-4 py-2.5">
          <p className={cn(styles.label, 'mb-2')}>
            {event.rsvpClosed ? 'Your reply' : 'Is your child coming?'}
          </p>
          <div className="flex flex-col gap-2">
            {event.children.map((child) => (
              <ChildReply key={child.studentId} eventId={event._id} child={child} closed={event.rsvpClosed} />
            ))}
          </div>
          {event.rsvpClosed && (
            <p className={cn(styles.meta, 'mt-2')}>Replies have closed for this event.</p>
          )}
        </div>
      )}
    </div>
  );
}

function ChildReply({
  eventId,
  child,
  closed,
}: {
  eventId: string;
  child: ParentCalendarEvent['children'][number];
  closed: boolean;
}) {
  const [respond, { isLoading }] = useRespondToEventMutation();
  const [error, setError] = useState('');

  const send = async (response: RsvpAnswer, guestCount = child.guestCount) => {
    setError('');
    try {
      await respond({ eventId, studentId: child.studentId, response, guestCount }).unwrap();
    } catch (err) {
      setError(getApiError(err));
    }
  };

  if (closed) {
    return (
      <div className="flex items-center justify-between gap-3">
        <span className="truncate text-[11.5px] font-semibold text-foreground">{child.studentName}</span>
        {child.response === 'going' ? (
          <Mark tone="success">Going{child.guestCount > 0 && ` +${child.guestCount}`}</Mark>
        ) : child.response === 'not_going' ? (
          <Mark tone="neutral">Not going</Mark>
        ) : (
          <span className={styles.meta}>No reply</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="truncate text-[11.5px] font-semibold text-foreground">{child.studentName}</span>
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            variant={child.response === 'going' ? 'primary' : 'secondary'}
            disabled={isLoading}
            onClick={() => void send('going')}
          >
            Going
          </Button>
          <Button
            size="sm"
            variant={child.response === 'not_going' ? 'primary' : 'secondary'}
            disabled={isLoading}
            onClick={() => void send('not_going', 0)}
          >
            Not going
          </Button>
        </div>
      </div>

      {/* Adults coming too — only worth asking once the answer is yes. */}
      {child.response === 'going' && (
        <div className="flex items-center justify-end gap-2">
          <span className={styles.meta}>Adults coming with them</span>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              aria-label={`One fewer adult for ${child.studentName}`}
              disabled={isLoading || child.guestCount === 0}
              onClick={() => void send('going', Math.max(0, child.guestCount - 1))}
            >
              −
            </Button>
            <span className={cn(styles.figure, 'w-5 text-center text-[12px]')}>{child.guestCount}</span>
            <Button
              size="sm"
              variant="ghost"
              iconOnly
              aria-label={`One more adult for ${child.studentName}`}
              disabled={isLoading || child.guestCount >= 20}
              onClick={() => void send('going', child.guestCount + 1)}
            >
              +
            </Button>
          </div>
        </div>
      )}

      {error && <p className="text-[10.5px] text-[hsl(var(--record-ink-danger))]">{error}</p>}
    </div>
  );
}
